/**
 * Einstieg in den Playtest: Liste der eigenen Lobbys und laufenden Playtests, „Neue Lobby",
 * Einladungscode eingeben. Wer genau eine wartende Lobby hat, landet ohne Umweg in ihr.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Swords, LogIn, Plus, Play, LogOut, XCircle } from 'lucide-react';
import Page from '../../../ui/Page';
import ActionBar from '../../../ui/ActionBar';
import SectionHeader from '../../../ui/SectionHeader';
import List from '../../../ui/List';
import Badge from '../../../ui/Badge';
import EmptyState from '../../../ui/EmptyState';
import Skeleton from '../../../ui/Skeleton';
import Input from '../../../ui/Input';
import ConfirmDialog from '../../../ui/ConfirmDialog';
import ButtonSpinner from '../../../ui/ButtonSpinner';
import { useToast } from '../../../ui/Toast';
import { json, apiClient } from '../../../api/apiClient';
import { errorMessage } from '../../../api/errorMessage';
import './PlaytestEntryPage.css';

const CODE_LENGTH = 6;
const SESSIONS_KEY = ['playtest-sessions'];

const initial = (name) => ((name || '?').trim().charAt(0) || '?').toUpperCase();

function sessionTitle(s) {
  return s.participantNames.length ? `Playtest mit ${s.participantNames.join(', ')}` : 'Playtest allein';
}

function sessionMeta(s) {
  const parts = [];
  if (s.status === 'waiting') parts.push(`${s.participantNames.length + 1} Teilnehmer`);
  if (s.ownDeckName) parts.push(`dein Deck: ${s.ownDeckName}`);
  return parts.join(' · ');
}

export default function PlaytestEntryPage() {
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();
  const { data: sessions, isPending, isError, refetch } = useQuery({
    queryKey: SESSIONS_KEY,
    queryFn: () => json('/api/playtest/sessions'),
    staleTime: 0,
  });

  const [code, setCode] = useState('');
  const [codeError, setCodeError] = useState(null);
  const [joining, setJoining] = useState(false);
  const [creating, setCreating] = useState(false);
  const [endTarget, setEndTarget] = useState(null);

  // Genau eine Session und sie wartet → ohne Umweg in die Lobby.
  useEffect(() => {
    if (sessions?.length === 1 && sessions[0].status === 'waiting') {
      navigate(`/playtest/lobby/${sessions[0].sessionId}`, { replace: true });
    }
  }, [sessions, navigate]);

  const ready = !isPending;
  const invalidate = () => queryClient.invalidateQueries({ queryKey: SESSIONS_KEY });

  const handleCreate = async () => {
    setCreating(true);
    try {
      const resp = await apiClient.post('/api/playtest/create', {});
      navigate(`/playtest/lobby/${resp.data.sessionId}`);
    } catch (err) {
      if (err?.code === 'ALREADY_HOSTING') { invalidate(); return; }
      toast.push({ tone: 'error', message: errorMessage(err, 'Lobby konnte nicht erstellt werden.') });
    } finally {
      setCreating(false);
    }
  };

  const handleJoin = async (e) => {
    e?.preventDefault();
    const trimmed = code.trim().toUpperCase();
    if (trimmed.length !== CODE_LENGTH) { setCodeError('Der Einladungscode hat 6 Zeichen.'); return; }
    setJoining(true);
    setCodeError(null);
    try {
      const resp = await apiClient.post('/api/playtest/join', { inviteCode: trimmed });
      navigate(`/playtest/lobby/${resp.data.sessionId}`);
    } catch (err) {
      setCodeError(errorMessage(err, 'Beitritt nicht möglich.'));
    } finally {
      setJoining(false);
    }
  };

  const handleLeave = async (s) => {
    try {
      await apiClient.post(`/api/playtest/${s.sessionId}/leave`);
      invalidate();
    } catch (err) {
      toast.push({ tone: 'error', message: errorMessage(err, 'Lobby konnte nicht verlassen werden.') });
    }
  };

  const handleEnd = async () => {
    const s = endTarget;
    setEndTarget(null);
    try {
      await apiClient.delete(`/api/playtest/${s.sessionId}`);
      invalidate();
    } catch (err) {
      toast.push({ tone: 'error', message: errorMessage(err, 'Lobby konnte nicht beendet werden.') });
    }
  };

  const sorted = [...(sessions || [])].sort((a, b) => {
    if (a.status !== b.status) return a.status === 'active' ? -1 : 1;
    return new Date(b.joinedAt) - new Date(a.joinedAt);
  });

  return (
    <Page>
      <ActionBar
        divider
        left={isPending
          ? <ActionBar.Stat id="playtests"><Skeleton width="90px" label="Playtests werden geladen" /></ActionBar.Stat>
          : <ActionBar.Stat id="playtests" value={sessions?.length ?? 0} label="Playtests" />}
        right={
          <>
            <form className="playtest-entry__join" onSubmit={handleJoin}>
              <Input
                className="playtest-entry__code"
                name="inviteCode"
                value={code}
                onChange={(e) => { setCode(e.target.value.toUpperCase()); setCodeError(null); }}
                onClear={() => { setCode(''); setCodeError(null); }}
                placeholder="ABC123"
                maxLength={CODE_LENGTH}
                autoComplete="off"
                spellCheck={false}
                aria-label="Einladungscode"
                invalid={!!codeError}
                disabled={!ready || joining}
              />
              <button type="submit" className="btn-secondary" disabled={!ready || joining || !code.trim()}>
                {joining ? <ButtonSpinner /> : <LogIn size={16} aria-hidden />} Beitreten
              </button>
            </form>
            <ActionBar.Action icon={Plus} label="Neue Lobby" variant="primary" disabled={!ready || creating} onClick={handleCreate} />
          </>
        }
      />

      {codeError && <p className="form-field__error playtest-entry__code-error" role="alert">{codeError}</p>}

      <SectionHeader>Deine Playtests</SectionHeader>

      {isPending && (
        <List>
          {Array.from({ length: 2 }).map((_, i) => (
            <List.Item key={i}>
              {/* Der Platzhalter deckt den Avatar-Kreis ganz ab — ein kleinerer ließe den
                  Accent-Ring darunter stehen und sähe nach Inhalt statt nach Laden aus. */}
              <List.Avatar><Skeleton width="40px" height="40px" radius="pill" /></List.Avatar>
              <List.Main><List.Name><Skeleton width="160px" /></List.Name></List.Main>
            </List.Item>
          ))}
        </List>
      )}

      {isError && (
        <EmptyState variant="error" icon={Swords} title="Playtests konnten nicht geladen werden" onRetry={refetch} />
      )}

      {ready && !isError && sorted.length === 0 && (
        <EmptyState icon={Swords} title="Kein Playtest offen" description="Eröffne eine neue Lobby oder gib einen Einladungscode ein." />
      )}

      {ready && !isError && sorted.length > 0 && (
        <List>
          {sorted.map((s) => (
            <List.Item key={s.sessionId}>
              <List.Avatar>{initial(s.hostName)}</List.Avatar>
              <List.Main>
                <List.Name>{sessionTitle(s)}</List.Name>
                {sessionMeta(s) && <List.Meta>{sessionMeta(s)}</List.Meta>}
              </List.Main>
              <List.Actions>
                {s.status === 'active' ? <Badge tone="accent">Läuft</Badge> : <Badge tone="pending">Wartet</Badge>}
                <button type="button" className="btn-primary" onClick={() => navigate(s.status === 'active' ? `/playtest/board/${s.sessionId}` : `/playtest/lobby/${s.sessionId}`)}>
                  <Play size={14} aria-hidden /> {s.status === 'active' ? 'Zurück zum Playtest' : 'Öffnen'}
                </button>
                {s.isHost ? (
                  <button type="button" className="btn-secondary" onClick={() => setEndTarget(s)}><XCircle size={14} aria-hidden /> Beenden</button>
                ) : s.status === 'waiting' && (
                  <button type="button" className="btn-secondary" onClick={() => handleLeave(s)}><LogOut size={14} aria-hidden /> Verlassen</button>
                )}
              </List.Actions>
            </List.Item>
          ))}
        </List>
      )}

      <ConfirmDialog
        open={!!endTarget}
        onOpenChange={(o) => { if (!o) setEndTarget(null); }}
        title={endTarget?.status === 'waiting' ? 'Lobby für alle beenden?' : 'Playtest beenden?'}
        description={endTarget?.status === 'waiting'
          ? 'Alle Teilnehmer verlieren diese Lobby; der Einladungscode wird ungültig.'
          : 'Alle Teilnehmer verlieren diesen Playtest.'}
        confirmLabel="Beenden"
        tone="danger"
        onConfirm={handleEnd}
      />
    </Page>
  );
}
